import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeProvider";
import ResponseLoader from "../Loader/ResponseLoader";
import { ExternalLink, Globe, Play } from "lucide-react";
import { extractDomain, fetchURLMetadata } from "../../utils/urlMetadata";
import { getDefaultTitleFromUrl } from "../../utils/helpers";
import logger from "../../services/Logger";

const URLPreview = ({ href, children }) => {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(false);
  const [faviconError, setFaviconError] = useState(false);
  const { theme } = useTheme();

  const defaultTitle = getDefaultTitleFromUrl(href);

  // Check if URL is likely to have rich metadata
  const isRichURL = (url) => {
    if (!url) return false;

    // Always try to fetch rich metadata for video platforms
    const videoPlatforms = ['youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com', 'twitch.tv'];
    if (videoPlatforms.some(platform => url.toLowerCase().includes(platform))) {
      return true;
    }

    // Skip if it's just a file extension or obvious direct file
    const fileExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.rar', '.tar', '.gz', '.mp4', '.avi', '.mov'];
    if (fileExtensions.some(ext => url.toLowerCase().includes(ext))) {
      return false;
    }

    return url.startsWith('https://') || url.startsWith('http://');
  };

  useEffect(() => {
    const setupMetadata = async () => {
      if (!isRichURL(href)) {
        setLoading(false);
        setError(true);
        return;
      }

      // Reset favicon error state for new URL
      setFaviconError(false);
      setLoading(true);

      try {
        // Check if this is a Vertex AI URL that needs redirect resolution
        const includeRedirectInfo = href.includes('vertexaisearch.cloud.google.com');

        // Fetch metadata from backend for individual links
        const backendMetadata = await fetchURLMetadata(href, includeRedirectInfo);

        if (backendMetadata) {
          setMetadata({
            title: backendMetadata.title || defaultTitle,
            description: backendMetadata.description || `Visit ${extractDomain(href)}`,
            domain: backendMetadata.domain || extractDomain(href),
            favicon: backendMetadata.favicon, // Use backend-provided favicon
            thumbnail: backendMetadata.thumbnail,
            author: backendMetadata.author,
            realUrl: backendMetadata.realUrl || href
          });
        } else {
          // Fallback metadata if backend fails
          const domain = extractDomain(href);
          setMetadata({
            title: defaultTitle,
            description: `Visit ${domain}`,
            domain: domain,
            favicon: null,
            realUrl: href
          });
        }
      } catch (error) {
        logger.warn('URLPreview', 'Failed to fetch metadata for URL', { href, error: error.message });
        // Fallback metadata on error
        const domain = extractDomain(href);
        setMetadata({
          title: defaultTitle,
          description: `Visit ${domain}`,
          domain: domain,
          favicon: null,
          realUrl: href
        });
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to avoid too rapid loading states
    const timer = setTimeout(setupMetadata, 300);
    return () => clearTimeout(timer);
  }, [href, children, defaultTitle]);

  // If not a rich URL, return simple link
  if (!isRichURL(href)) {
    return (
      <a
        href={href}
        className="inline-flex items-center gap-1.5 px-2 py-1 bg-fill-gray-50 dark:bg-fill-gray-700 text-ink-gray-600 dark:text-ink-gray-300 hover:bg-fill-gray-100 dark:hover:bg-fill-gray-600 rounded-md border border-edge-gray-200 dark:border-edge-gray-500 transition-colors duration-200 text-sm"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Globe className="w-4 h-4" />
        {defaultTitle}
        <ExternalLink className="w-3 h-3" />
      </a>
    );
  }

  // Loading state
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-fill-gray-50 dark:bg-fill-gray-700 text-ink-gray-600 dark:text-ink-gray-300 rounded-md border border-edge-gray-200 dark:border-edge-gray-500 text-sm">
        <Globe className="w-4 h-4" />
        {defaultTitle}
        <ResponseLoader theme={theme} />
      </span>
    );
  }

  // Rich preview - enhanced for videos and regular URLs
  const isVideo = metadata?.domain?.includes('youtube.com') || metadata?.domain?.includes('vimeo.com') || metadata?.video_id;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-2 py-1 bg-fill-blue-50 dark:bg-fill-hex-172554/50 text-ink-blue-600 dark:text-ink-blue-300 hover:bg-fill-blue-100 dark:hover:bg-fill-blue-900/50 rounded-md border border-edge-blue-200 dark:border-edge-blue-600 transition-colors duration-200 text-sm"
      title={metadata?.description || metadata?.title}
    >
      {/* Show video thumbnail for YouTube/Vimeo, favicon for others */}
      {isVideo && metadata?.thumbnail ? (
        <img
          src={metadata.thumbnail}
          alt={`${metadata.title} thumbnail`}
          className="w-6 h-4 rounded object-cover"
          onError={() => setFaviconError(true)}
        />
      ) : metadata?.favicon && !faviconError ? (
        <img
          src={metadata.favicon}
          alt={`${metadata.domain} favicon`}
          className="w-4 h-4 rounded"
          onError={() => setFaviconError(true)}
        />
      ) : isVideo ? (
        <Play className="w-4 h-4" />
      ) : (
        <Globe className="w-4 h-4" />
      )}

      {/* Title with author for videos */}
      <span className="truncate max-w-[200px]">
        {metadata?.title || defaultTitle}
        {metadata?.author && (
          <span className="text-xs opacity-75"> by {metadata.author}</span>
        )}
      </span>

      <ExternalLink className="w-3 h-3 flex-shrink-0" />
    </a>
  );
};

export default URLPreview;
